/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import {
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  IconButton,
} from '@mui/material';
import { useState } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Styles from '../../css/AutopayPage.module.css';
import { useAutopayContext } from '../../contexts/AutopayContext';
import AutopayModal from '../modals/AutopayModal';
import CancelAutopayModal from '../modals/CancelAutopayModal';
import { NumericFormat, PatternFormat } from 'react-number-format';
import { isStrEmptyOrNull, isValidRoutingNumber } from '../../common/UtilityFunctions';
import { useRootContext } from '../../contexts/RootContext';
import { useAuthContext } from '../../contexts/AuthContext';
import ButtonBorder from '../ButtonBorder';
import LoadingModal from '../modals/LoadingModal';
import React from 'react';
import ButtonBorderWrapper from '../../components/ButtonBorderWrapper';
import EnvelopeWithBox from '../../components/EnvelopeWithBox';
import useWindowDimensions from '../../common/hooks/useWindowDimensions';
import { useViewingContext } from '../../contexts/ViewingContext';
import { InfoOutlined } from '@mui/icons-material';

const AutopayForm = () => {
  const { isMobile } = useWindowDimensions();
  const autopayContext = useAutopayContext();
  const rootContext = useRootContext();
  const { fullAccess } = useAuthContext();
  const { isServicerView } = useViewingContext();
  const [verified, setVerified] = useState(false);
  dayjs.extend(utc);
  dayjs.extend(timezone);
  const dateTime = new Date();

  return (
    <ButtonBorderWrapper>
      <EnvelopeWithBox blockName={isServicerView ? 'Auto Pay' : 'Setup Recurring Payments'}>
        <form onSubmit={autopayContext.validateFields}>
          {autopayContext.autopayModalOpen && <AutopayModal content={autopayContext.autopayModalContent} />}
          {autopayContext.cancelAutopayModalOpen && <CancelAutopayModal />}
          {autopayContext.status === 'loading' && (
            <LoadingModal open={autopayContext.status === 'loading'} loadingMessage={autopayContext.loadingMessage} />
          )}
          <Snackbar
            open={!!autopayContext.statusAlert.message}
            onClose={() =>
              autopayContext.setStatusAlert({
                status: '',
                message: '',
              })
            }
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            ClickAwayListenerProps={{ onClickAway: () => null }}
          >
            <Alert
              onClose={() =>
                autopayContext.setStatusAlert({
                  status: '',
                  message: '',
                })
              }
              severity={autopayContext.statusAlert.status as any}
              variant='filled'
              className={Styles.alert}
            >
              {autopayContext.statusAlert.message}
            </Alert>
          </Snackbar>
          {isServicerView && isMobile && (
            <div className={Styles.stackContainer}>
              <div className={`${Styles.flexColServicer} ${Styles.businessText}`}>
                <h3 className={Styles.businessHeader}>
                  Verbal Authorization and Disclosures Required Prior to and During Auto Pay Set Up
                </h3>
                <div className={Styles.textSection}>
                  {autopayContext.stopCodeST04 && (
                    <p className={Styles.warningText}>
                      <IconButton
                        className={Styles.icon}
                        onClick={() => {
                          autopayContext.setAutopayModalContent('stopCodeST04');
                          autopayContext.setAutopayModalOpen(true);
                        }}
                      >
                        <InfoOutlined className={Styles.icon} />
                      </IconButton>
                      ST04: Payment Freeze - Autopay cannot be set up.
                    </p>
                  )}
                  <br />
                  <fieldset>
                    <legend className={Styles.boldText}>Prior to setup - Obtain verbal authorization</legend>
                    <p className={Styles.preLineText}>
                      {'Before we can set up your auto pay, I must ask if you agree to provide your authorization to initiate a recurring automatic' +
                        ' withdrawal over the phone. If you agree, please state "I agree”.'}
                    </p>
                    <br />
                    <p className={Styles.boldText}>
                      {'You must wait for borrower to say "I agree". If the borrower replies with another affirmative statement, the associate should' +
                        ' inform the borrower that auto pay cannot be established unless the borrower states "I agree".'}
                    </p>
                    <br />
                    <p className={Styles.preLineText}>
                      {'Do you certify that you are an authorized signer on the bank account you have provided today?'}
                    </p>
                    <br />
                    <p className={Styles.boldText}>
                      {
                        'After the borrower confirms that he/she is an authorized signer on the bank account, read the following disclosure:'
                      }
                    </p>
                    <br />
                    <p className={Styles.preLineText}>
                      {'If the account information provided is not accurate or if this payment gets returned by your bank as "unable to locate",' +
                        ' "invalid account number", "insufficient" or "uncollected funds", Sun West Mortgage will not be held responsible for any fee(s)' +
                        ' assessed or negative credit reporting, and all future dated electronic payments may be deleted and not processed.'}
                    </p>
                  </fieldset>
                  <br />
                  <p className={Styles.boldText}>During setup - Verbal disclosures required:</p>
                  <p className={Styles.preLineText}>
                    {'I also want to confirm that today '} <strong>{dateTime.toDateString()}</strong>
                    {', you '}
                    <strong>{autopayContext.borrowerInfo?.fullName}</strong>
                    {
                      ', are authorizing Sun West Mortgage Company, Inc. to initiate a recurring automatic withdrawal from your bank account ending in '
                    }
                    <strong>{autopayContext.autopayInfo.eftBankAccountNo.slice(-4)}</strong>
                    {' for '}
                    <strong>
                      $
                      {(
                        autopayContext.payment +
                        autopayContext.autopayInfo?.additionalEscrow +
                        autopayContext.autopayInfo?.additionalPrincipal
                      ).toFixed(2)}
                    </strong>
                    {' on the '}
                    <strong>{autopayContext.autopayInfo.settleDay}</strong>
                    {' of each month starting on '}
                    <strong>
                      {autopayContext.autopayInfo?.startDate
                        ? dayjs.unix(autopayContext.autopayInfo?.startDate).format('ddd, MMM D, YYYY')
                        : ''}
                    </strong>
                    {' and ending when you notify us to cancel your auto pay, or when your loan is paid in full.\n\n' +
                      'Please be advised that the auto pay will automatically adjust if your monthly mortgage payment increases or decreases for' +
                      ' any reason. Any changes to the schedule including cancelation must be requested 3 business days prior to the next' +
                      ' scheduled withdrawal date.'}
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className={isServicerView ? Styles.flexRow : Styles.flexCol}>
            {!isServicerView && (
              <>
                {autopayContext.stopCodeST04 && (
                  <p className={Styles.warningText}>
                    <IconButton
                      className={Styles.icon}
                      onClick={() => {
                        autopayContext.setAutopayModalContent('stopCodeST04');
                        autopayContext.setAutopayModalOpen(true);
                      }}
                    >
                      <InfoOutlined className={Styles.icon} />
                    </IconButton>
                    Autopay cannot be set up.
                  </p>
                )}
                <br />
                <p className={Styles.text}>
                  {'Thank you for choosing the recurring electronic monthly payments (auto pay) option. Please allow up to 3 business days' +
                    ' for the auto pay to be set up. Once the auto pay setup is complete, you will receive a letter of confirmation of your' +
                    ' enrollment from Sun West Mortgage Company, Inc. Until the auto pay begins, please make manual payments.\n\n' +
                    'Note:\n\n' +
                    '1. There is no fee for the monthly automatic withdrawal. You may select any day from the first sixteen calendar days as "' +
                    'Day of Month to Draft".\n\n' +
                    '2. The payment will be drafted each month on the day your account is setup for automatic withdrawal. If that day falls on a' +
                    ' non-business day, the payment will be drafted on the next business day.\n\n' +
                    '3. The amount drafted monthly will automatically reflect the change in your monthly payment due to interest rate adjustments' +
                    ' according to the terms of your mortgage note and/or annual escrow adjustments (if applicable). You will receive return' +
                    ' notification of the change in your monthly payments.\n\n' +
                    '4. If the draft fails due to insufficient funds, invalid account number, closed account or stop payment; you will be responsible' +
                    ' for the return check fee and any late fees or negative credit reporting which occurs as a result of the returned payment.\n\n' +
                    'The information here may be changed at any time after the original auto pay set-up. Any changes made here after 3:00PM ET will' +
                    ' be effective the next business day. If you have any questions feel free to contact our customer service at 800-345-7884 (Ext: 7882)' +
                    ' between 8:30AM and 5:00PM PT Monday-Friday. (Except Federal Holidays)\n\n'}
                </p>
                <h3 className={Styles.infoLabel}>Please complete the following information:</h3>
              </>
            )}
            <>
              {!isServicerView && (
                <p className={Styles.text}>
                  Autopay Status: <strong>{autopayContext.borrowerAutopayStatus}</strong>
                </p>
              )}
              <div className={isServicerView && !isMobile ? Styles.stackContainer : Styles.gridContainer}>
                {isServicerView && (
                  <FormControl fullWidth size='small' error={autopayContext.autopayStatusError}>
                    <InputLabel>Loan EFT Flag *</InputLabel>
                    <Select
                      label='Loan EFT Flag *'
                      disabled={!fullAccess && isServicerView}
                      value={autopayContext.autopayInfo?.autoPayStatusKeyword}
                      onChange={(e) => {
                        autopayContext.dispatchAutopayInfo({
                          type: 'changed_property',
                          property: { autoPayStatusKeyword: e.target.value },
                        });
                        if (autopayContext.autopayStatusError) {
                          autopayContext.setAutopayStatusError(false);
                        }
                      }}
                    >
                      {autopayContext.autopayStatusQidInfo.map((option, key) => (
                        <MenuItem key={key} value={option.keyword}>
                          {option.description}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {autopayContext.autopayStatusError ? 'Autopay Status Required' : ''}
                    </FormHelperText>
                  </FormControl>
                )}
                <div>
                  <PatternFormat
                    fullWidth
                    size='small'
                    label='Transit Routing No *'
                    placeholder='Transit Routing No *'
                    customInput={TextField}
                    format='#########'
                    inputProps={{ inputMode: 'numeric' }}
                    error={autopayContext.eftRoutingNoError}
                    helperText={autopayContext.eftRoutingNoError ? 'Invalid Routing Number' : ''}
                    value={autopayContext.autopayInfo?.eftRoutingNo}
                    onChange={(e) => {
                      autopayContext.dispatchAutopayInfo({
                        type: 'changed_property',
                        property: { eftRoutingNo: e.target.value },
                      });
                      if (autopayContext.eftRoutingNoError && isValidRoutingNumber(e.target.value)) {
                        autopayContext.setEftRoutingNoError(false);
                      }
                    }}
                  />
                  {!isServicerView && (
                    <Button className={Styles.helpButton} onClick={rootContext.scrollToElement}>
                      Help
                    </Button>
                  )}
                </div>
                <TextField
                  fullWidth
                  size='small'
                  label='Bank Name *'
                  placeholder='Bank Name *'
                  error={autopayContext.eftBankNameError}
                  helperText={autopayContext.eftBankNameError ? 'Bank Name Required' : ''}
                  value={autopayContext.autopayInfo?.eftBankName}
                  onChange={(e) => {
                    autopayContext.dispatchAutopayInfo({
                      type: 'changed_property',
                      property: { eftBankName: e.target.value },
                    });
                    if (autopayContext.eftBankNameError && !isStrEmptyOrNull(e.target.value)) {
                      autopayContext.setEftBankNameError(false);
                    }
                  }}
                />
                <div>
                  <TextField
                    fullWidth
                    size='small'
                    label='Bank Account No *'
                    placeholder='Bank Account No *'
                    error={autopayContext.eftBankAccountNoError}
                    helperText={autopayContext.eftBankAccountNoError ? 'Bank Account No Required' : ''}
                    value={autopayContext.autopayInfo?.eftBankAccountNo}
                    onChange={(e) => {
                      autopayContext.dispatchAutopayInfo({
                        type: 'changed_property',
                        property: { eftBankAccountNo: e.target.value },
                      });
                      if (autopayContext.eftBankAccountNoError && !isStrEmptyOrNull(e.target.value)) {
                        autopayContext.setEftBankAccountNoError(false);
                      }
                    }}
                  />
                  {!isServicerView && (
                    <div className={Styles.flexRow}>
                      <p className={Styles.helpText}>{'Include leading zeros if present. '}</p>
                      <Button className={Styles.helpButton} onClick={rootContext.scrollToElement}>
                        Help
                      </Button>
                    </div>
                  )}
                </div>
                <FormControl fullWidth size='small' error={autopayContext.eftBankAccountTypeError}>
                  <InputLabel>Bank Account Type *</InputLabel>
                  <Select
                    label='Bank Account Type *'
                    disabled={!fullAccess && isServicerView}
                    value={autopayContext.autopayInfo?.eftBankAccountTypeKeyword}
                    onChange={(e) => {
                      autopayContext.dispatchAutopayInfo({
                        type: 'changed_property',
                        property: { eftBankAccountTypeKeyword: e.target.value },
                      });
                      if (autopayContext.eftBankAccountTypeError) {
                        autopayContext.setEftBankAccountTypeError(false);
                      }
                    }}
                  >
                    {autopayContext.eftBankAcctTypeQidInfo.map((option, key) => (
                      <MenuItem key={key} value={option.keyword}>
                        {option.description}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {autopayContext.eftBankAccountTypeError ? 'Account Type Required' : ''}
                  </FormHelperText>
                </FormControl>
                {!isServicerView && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={autopayContext.retainInfo}
                        onChange={() => autopayContext.setRetainInfo(!autopayContext.retainInfo)}
                      />
                    }
                    className={Styles.controlLabel}
                    label='Click here to store this account information for one time payments'
                  />
                )}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <FormControl fullWidth>
                    <DatePicker
                      label='Start Date *'
                      slotProps={{
                        textField: {
                          helperText: autopayContext.startDateError
                            ? 'Start Date Must Be The First Of The Current Or An Upcoming Month'
                            : '',
                          error: autopayContext.startDateError,
                          size: 'small',
                        },
                      }}
                      minDate={dayjs().startOf('month')}
                      shouldDisableDate={(date) => date.get('date') !== 1}
                      value={
                        autopayContext.autopayInfo?.startDate ? dayjs.unix(autopayContext.autopayInfo?.startDate) : null
                      }
                      onChange={(value) => {
                        autopayContext.dispatchAutopayInfo({
                          type: 'changed_property',
                          property: { startDate: dayjs(value).tz('America/Los_Angeles', true).unix() },
                        });
                        if (!autopayContext.prevStartDate) {
                          autopayContext.setPrevStartDate(autopayContext.autopayInfo.startDate);
                        }
                        if (autopayContext.startDateError) {
                          autopayContext.setStartDateError(false);
                        }
                      }}
                    />
                  </FormControl>
                </LocalizationProvider>
                {isServicerView && (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <FormControl fullWidth>
                      <DatePicker
                        label='Last Transmit Date'
                        disabled
                        slotProps={{
                          textField: {
                            size: 'small',
                          },
                        }}
                        value={autopayContext.latestPaymentDate ? dayjs.unix(autopayContext.latestPaymentDate) : null}
                      />
                    </FormControl>
                  </LocalizationProvider>
                )}
                <NumericFormat
                  fullWidth
                  size='small'
                  label='Day of Month to Draft *'
                  placeholder='Day of Month to Draft *'
                  customInput={TextField}
                  isAllowed={(values) => {
                    const { floatValue, formattedValue } = values;
                    if (!formattedValue) {
                      return true;
                    }
                    return floatValue >= 1 && floatValue <= 31;
                  }}
                  decimalScale={0}
                  inputProps={{ inputMode: 'numeric' }}
                  error={
                    autopayContext.settleDayError ||
                    autopayContext.settleDayCurrentMonthError ||
                    autopayContext.settleDayRangeError
                  }
                  helperText={
                    autopayContext.settleDayError
                      ? 'Settle Day Required'
                      : autopayContext.settleDayCurrentMonthError
                        ? 'Settle Day must be in the future when starting in the current month'
                        : autopayContext.settleDayRangeError
                          ? 'Value must be 1-16'
                          : ''
                  }
                  value={autopayContext.autopayInfo?.settleDay}
                  onChange={(e) => {
                    autopayContext.dispatchAutopayInfo({
                      type: 'changed_property',
                      property: { settleDay: e.target.value },
                    });
                    if (autopayContext.settleDayError) {
                      autopayContext.setSettleDayError(false);
                    }
                    if (autopayContext.settleDayRangeError && parseInt(e.target.value) < 17) {
                      autopayContext.setSettleDayRangeError(false);
                    }
                  }}
                />
                {isServicerView && (
                  <NumericFormat
                    disabled
                    fullWidth
                    size='small'
                    label='EFT Payment'
                    customInput={TextField}
                    thousandSeparator={true}
                    prefix={'$'}
                    decimalScale={2}
                    fixedDecimalScale={true}
                    value={autopayContext.payment}
                  />
                )}
                <NumericFormat
                  fullWidth
                  size='small'
                  placeholder='Additional Principal'
                  label='Additional Principal'
                  inputProps={{ inputMode: 'numeric' }}
                  value={autopayContext.autopayInfo?.additionalPrincipal}
                  error={autopayContext.additionalPrincipalError}
                  customInput={TextField}
                  thousandSeparator={true}
                  prefix={'$'}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  allowNegative={false}
                  helperText={autopayContext.additionalPrincipalError ? 'Additional Principal Required' : ''}
                  onValueChange={(values) => {
                    autopayContext.dispatchAutopayInfo({
                      type: 'changed_property',
                      property: { additionalPrincipal: values.floatValue },
                    });
                    if (autopayContext.additionalPrincipalError) {
                      autopayContext.setAdditionalPrincipalError(false);
                    }
                  }}
                />
                <div />
                <NumericFormat
                  fullWidth
                  size='small'
                  label='Additional Escrow'
                  placeholder='Additional Escrow'
                  inputProps={{ inputMode: 'numeric' }}
                  value={autopayContext.autopayInfo?.additionalEscrow}
                  error={autopayContext.additionalEscrowError}
                  customInput={TextField}
                  thousandSeparator={true}
                  prefix={'$'}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  allowNegative={false}
                  helperText={autopayContext.additionalEscrowError ? 'Additional Escrow Required' : ''}
                  onValueChange={(values) => {
                    autopayContext.dispatchAutopayInfo({
                      type: 'changed_property',
                      property: { additionalEscrow: values.floatValue },
                    });
                    if (autopayContext.additionalEscrowError) {
                      autopayContext.setAdditionalEscrowError(false);
                    }
                  }}
                />
                {!isServicerView && (
                  <FormControlLabel
                    control={<Checkbox required />}
                    className={Styles.controlLabel}
                    value={verified}
                    onClick={() => setVerified((prev) => !prev)}
                    label='I confirm that I am the authorized signer on the bank account provided.'
                  />
                )}
                {isServicerView && (
                  <>
                    <NumericFormat
                      disabled
                      fullWidth
                      size='small'
                      label='Total EFT Payment'
                      customInput={TextField}
                      thousandSeparator={true}
                      prefix={'$'}
                      decimalScale={2}
                      fixedDecimalScale={true}
                      value={
                        autopayContext.payment +
                        autopayContext.autopayInfo?.additionalEscrow +
                        autopayContext.autopayInfo?.additionalPrincipal
                      }
                    />
                    <FormControl fullWidth size='small' error={autopayContext.authMethodError}>
                      <InputLabel>Method Of Authorization *</InputLabel>
                      <Select
                        label='Method Of Authorization *'
                        disabled={!fullAccess && isServicerView}
                        value={autopayContext.autopayInfo?.authorizationMethodKeyword}
                        onChange={(e) => {
                          autopayContext.dispatchAutopayInfo({
                            type: 'changed_property',
                            property: { authorizationMethodKeyword: e.target.value },
                          });
                          if (autopayContext.authMethodError) {
                            autopayContext.setAuthMethodError(false);
                          }
                        }}
                      >
                        {autopayContext.authMethodQidInfo.map((option, key) => (
                          <MenuItem key={key} value={option.keyword}>
                            {option.description}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {autopayContext.authMethodError ? 'Authorization Method Required' : ''}
                      </FormHelperText>
                    </FormControl>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <FormControl fullWidth>
                        <DatePicker
                          label='Authorization Date *'
                          slotProps={{
                            textField: {
                              helperText: autopayContext.authDateError ? 'Authorization Date Required' : '',
                              error: autopayContext.authDateError,
                              size: 'small',
                            },
                          }}
                          value={
                            autopayContext.autopayInfo?.authorizationDate
                              ? dayjs.unix(autopayContext.autopayInfo?.authorizationDate)
                              : null
                          }
                          onChange={(value) => {
                            autopayContext.dispatchAutopayInfo({
                              type: 'changed_property',
                              property: { authorizationDate: dayjs(value).unix() },
                            });
                            if (autopayContext.authDateError) {
                              autopayContext.setAuthDateError(false);
                            }
                          }}
                        />
                      </FormControl>
                    </LocalizationProvider>
                  </>
                )}
              </div>
            </>
            {isServicerView && !isMobile && (
              <div className={`${Styles.flexCol} ${Styles.businessText}`}>
                <h3 className={Styles.businessHeader}>
                  Verbal Authorization and Disclosures Required Prior to and During Auto Pay Set Up
                </h3>
                <div className={Styles.textSection}>
                  {autopayContext.stopCodeST04 && (
                    <p className={Styles.warningText}>
                      <IconButton className={Styles.icon} onClick={() => autopayContext.setAutopayModalOpen(true)}>
                        <InfoOutlined className={Styles.icon} />
                      </IconButton>
                      ST04: Payment Freeze - Autopay cannot be set up.
                    </p>
                  )}
                  <br />
                  <fieldset>
                    <legend className={Styles.boldText}>Prior to setup - Obtain verbal authorization</legend>
                    <p className={Styles.preLineText}>
                      {'Before we can set up your auto pay, I must ask if you agree to provide your authorization to initiate a recurring automatic' +
                        ' withdrawal over the phone. If you agree, please state "I agree”.'}
                    </p>
                    <br />
                    <p className={Styles.boldText}>
                      {'You must wait for borrower to say "I agree". If the borrower replies with another affirmative statement, the associate should' +
                        ' inform the borrower that auto pay cannot be established unless the borrower states "I agree".'}
                    </p>
                    <br />
                    <p className={Styles.preLineText}>
                      {'Do you certify that you are an authorized signer on the bank account you have provided today?'}
                    </p>
                    <br />
                    <p className={Styles.boldText}>
                      {
                        'After the borrower confirms that he/she is an authorized signer on the bank account, read the following disclosure:'
                      }
                    </p>
                    <br />
                    <p className={Styles.preLineText}>
                      {'If the account information provided is not accurate or if this payment gets returned by your bank as "unable to locate",' +
                        ' "invalid account number", "insufficient" or "uncollected funds", Sun West Mortgage will not be held responsible for any fee(s)' +
                        ' assessed or negative credit reporting, and all future dated electronic payments may be deleted and not processed.'}
                    </p>
                  </fieldset>
                  <br />
                  <p className={Styles.boldText}>During setup - Verbal disclosures required:</p>
                  <p className={Styles.preLineText}>
                    {'I also want to confirm that today '} <strong>{dateTime.toDateString()}</strong>
                    {', you '}
                    <strong>{autopayContext.borrowerInfo?.fullName}</strong>
                    {
                      ', are authorizing Sun West Mortgage Company, Inc. to initiate a recurring automatic withdrawal from your bank account ending in '
                    }
                    <strong>{autopayContext.autopayInfo.eftBankAccountNo.slice(-4)}</strong>
                    {' for '}
                    <strong>
                      $
                      {(
                        autopayContext.payment +
                        autopayContext.autopayInfo?.additionalEscrow +
                        autopayContext.autopayInfo?.additionalPrincipal
                      ).toFixed(2)}
                    </strong>
                    {' on the '}
                    <strong>{autopayContext.autopayInfo.settleDay}</strong>
                    {' of each month starting on '}
                    <strong>
                      {autopayContext.autopayInfo?.startDate
                        ? dayjs.unix(autopayContext.autopayInfo?.startDate).format('ddd, MMM D, YYYY')
                        : ''}
                    </strong>
                    {' and ending when you notify us to cancel your auto pay, or when your loan is paid in full.\n\n' +
                      'Please be advised that the auto pay will automatically adjust if your monthly mortgage payment increases or decreases for' +
                      ' any reason. Any changes to the schedule including cancelation must be requested 3 business days prior to the next' +
                      ' scheduled withdrawal date.'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </form>
      </EnvelopeWithBox>
      <ButtonBorder>
        <Button
          type='submit'
          className={Styles.button}
          disabled={
            (!fullAccess && isServicerView) ||
            (autopayContext.autopayInfo.autoPayStatusKeyword !== 'YES' &&
              autopayContext.autopayInfo.autoPayStatusKeyword !== 'PREPAY' &&
              autopayContext.hasDisablingStopCode) ||
            (!isServicerView && !verified)
          }
          onClick={autopayContext.validateFields}
        >
          Next
        </Button>
        {!isServicerView &&
        autopayContext.autopayInfo.autoPayStatusKeyword &&
        (autopayContext.autopayInfo.autoPayStatusKeyword === 'YES' ||
          autopayContext.autopayInfo.autoPayStatusKeyword === 'PREPAY') ? (
          <Button
            className={Styles.cancelButton}
            variant='contained'
            onClick={() => autopayContext.setCancelAutopayModalOpen(true)}
          >
            Cancel
          </Button>
        ) : (
          React.Fragment
        )}
        {autopayContext.originalAutopayStatus === 'CANCELED' &&
        autopayContext.originalBankingDetails &&
        (autopayContext.originalBankingDetails.eftBankAccountNo ||
          autopayContext.originalBankingDetails.eftBankAccountTypeKeyword ||
          autopayContext.originalBankingDetails.eftBankName ||
          autopayContext.originalBankingDetails.eftRoutingNo) ? (
          <Button
            className={Styles.cancelButton}
            variant='contained'
            onClick={() => {
              autopayContext.setAutopayModalContent('removeBankDetails');
              autopayContext.setAutopayModalOpen(true);
            }}
          >
            Remove Bank Details
          </Button>
        ) : (
          React.Fragment
        )}
      </ButtonBorder>
    </ButtonBorderWrapper>
  );
};

export default AutopayForm;
